import { observer } from 'mobx-react';
import { Card, Switch, Typography, message, Space } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text } = Typography;

// Interactive twin of KpiTile — replaces the static AUDIENCE card on the
// Overview row. Audience is a binary pair (patientApp / merchantApp), so
// inline toggles are the right surface: one click flips visibility without
// the user jumping to a different tab.
//
// Same visual recipe as SkillRouterTile so the row reads as a coherent
// "controls" group rather than a mix of static counts + interactive widgets.
//
// Why not "navigate to Advanced"?
//   The Advanced tab carries tuning knobs (consultation, memory strategy,
//   creativity) — audience never lived there. Pointing the user there from
//   the KPI row was a dead-end. An inline toggle removes the round-trip.

export const AudienceTile = observer(function AudienceTile() {
  const { agentDetailStore: detail, themeStore } = useStore();
  const agent = detail.agent;
  const isDark = themeStore.mode === 'dark';
  const patient = !!agent?.patientApp;
  const care = !!agent?.merchantApp;
  const exposed = patient || care;
  const saving = detail.isSavingAudience;

  // Keep the visual rhythm consistent with KpiTile (same min-height, padding,
  // font sizes, gradient pattern). The "lit" state mirrors SkillRouterTile so
  // the row's interactive cards visually agree on the on/off encoding.
  const bg = exposed
    ? isDark
      ? `linear-gradient(135deg, ${colors.green[700]}33 0%, ${colors.slate[800]} 70%)`
      : `linear-gradient(135deg, ${colors.green[100]} 0%, #FFFFFF 70%)`
    : isDark
      ? `linear-gradient(135deg, ${colors.slate[700]}55 0%, ${colors.slate[800]} 100%)`
      : `linear-gradient(135deg, ${colors.slate[50]} 0%, #FFFFFF 100%)`;

  const border = exposed
    ? isDark ? colors.green[600] + '88' : colors.green[100]
    : isDark ? colors.slate[700] : colors.slate[200];

  const accentColor = exposed
    ? isDark ? colors.green[100] : colors.green[600]
    : isDark ? colors.slate[400] : colors.slate[500];

  const labelColor = isDark ? colors.slate[400] : colors.slate[500];
  const headlineColor = isDark ? colors.slate[50] : colors.slate[900];
  const hintColor = isDark ? colors.slate[400] : colors.slate[500];

  const flip = async (field: 'patientApp' | 'merchantApp', next: boolean, label: string) => {
    try {
      await detail.setAudienceFlag(field, next);
      void message.success(`${label} ${next ? 'enabled' : 'disabled'}`);
    } catch (e) {
      void message.error(e instanceof Error ? e.message : `Could not update ${label.toLowerCase()}`);
    }
  };

  return (
    <Card
      styles={{
        body: {
          padding: 18,
          minHeight: 120,
          background: bg,
          border: `1px solid ${border}`,
          borderRadius: 12,
          position: 'relative',
          overflow: 'hidden',
          transition: 'background 200ms ease, border-color 200ms ease',
        },
      }}
      style={{ border: 'none', cursor: 'default' }}
    >
      {/* Top row: label + help hotspot + tile icon */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <Text
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            color: labelColor,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          Audience
          <HelpHotspot topic="agents.audience" variant="inline" label="Learn about audience" />
        </Text>
        <div style={{ color: accentColor, opacity: 0.6, fontSize: 18, lineHeight: 1 }}>
          <TeamOutlined />
        </div>
      </div>

      {/* Inline toggles — one row per audience surface, switch on the right */}
      <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ToggleRow
          label="Patient"
          on={patient}
          headlineColor={headlineColor}
          hintColor={hintColor}
          loading={saving}
          onChange={(v) => void flip('patientApp', v, 'Patient surface')}
        />
        <ToggleRow
          label="Care team"
          on={care}
          headlineColor={headlineColor}
          hintColor={hintColor}
          loading={saving}
          onChange={(v) => void flip('merchantApp', v, 'Care team surface')}
        />
      </div>

      {/* Bottom hint — explains the consequence, like SkillRouterTile */}
      <div style={{ marginTop: 10 }}>
        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4, color: hintColor }}>
          {exposed ? (
            <Space size={4}>
              Visible to:
              {patient && <strong style={{ color: accentColor }}>Patient</strong>}
              {patient && care && <span>&amp;</span>}
              {care && <strong style={{ color: accentColor }}>Care team</strong>}
            </Space>
          ) : (
            'Internal only — neither surface advertises this agent.'
          )}
        </Text>
      </div>
    </Card>
  );
});

function ToggleRow({
  label,
  on,
  headlineColor,
  hintColor,
  loading,
  onChange,
}: {
  label: string;
  on: boolean;
  headlineColor: string;
  hintColor: string;
  loading: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
      }}
    >
      <Text
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: on ? headlineColor : hintColor,
        }}
      >
        {label}
      </Text>
      <Switch size="small" checked={on} loading={loading} onChange={onChange} />
    </div>
  );
}
